/*
 *    Copyright [2021] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       http://www.wisemapping.org/license
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import { json } from 'react-router-dom';
import { ErrorInfo, MapMetadata } from '../../classes/client';
import { EditorRenderMode } from '@wisemapping/editor';
import AppConfig from '../../classes/app-config';

export type EditorMetadata = {
  editorMode: EditorRenderMode;
  mapMetadata: MapMetadata;
  zoom: number;
};

export type PageModeType = 'edit' | 'try' | 'view-public' | 'view-private';

export const loader = (pageMode: PageModeType) => {
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  return async ({ params }): Promise<Response> => {
    const client = AppConfig.getClient();
    client.onSessionExpired;
    let result: Response | undefined;
    const mapId = Number.parseInt(params.id);

    switch (pageMode) {
      case 'try': {
        // For 'try' mode, we're using hardcoded values so no need for complex parsing
        // But we'll use the same structure for consistency
        const value = await Promise.resolve({
          editorMode: 'showcase',
          mapMetadata: {
            id: mapId,
            title: 'What is WiseMapping ?',
            creatorFullName: 'Paulo Gustavo Veiga',
            isLocked: false,
            jsonProps: '{ "zoom": 0.8 }',
          },
          zoom: 0.8,
        });
        result = json(value);
        break;
      }
      case 'edit':
      case 'view-private': {
        try {
          const data = await Promise.all([
            client.fetchMapMetadata(mapId),
            client.fetchMapInfo(mapId),
          ]).then((values) => {
            console.log("values===>",values);
            const [mapMedata, mapInfo] = values;

            console.log("mapMedata====>",mapMedata);
            console.log("mapInfo====>",mapInfo);
            

            let editorMode: EditorRenderMode;
            if (mapMedata.isLocked || pageMode === 'view-private') {
              editorMode = 'viewonly-private';
            } else {
              editorMode = `edition-${mapInfo.role}`;
            }

            // Build result ...
            console.log("mapMedata.jsonProps===>",mapMedata.jsonProps);
            
            // Parse jsonProps safely with fallback
            let zoom = 0.8; // Default zoom value
            try {
              // Try standard JSON parsing first
              const jsonProps = JSON.parse(mapMedata.jsonProps);
              zoom = jsonProps.zoom || zoom;
            } catch (jsonError) {
              console.warn("Invalid JSON format in jsonProps, attempting to fix:", jsonError);
              
              // Try to fix common JSON formatting issues
              try {
                // Fix for format like {zoom:0.8} (missing quotes around property names)
                const fixedJson = mapMedata.jsonProps
                  .replace(/\{/g, '{"')
                  .replace(/:/g, '":')
                  .replace(/":/g, '":')
                  .replace(/,/g, ',"')
                  .replace(/,"/g, ',"')
                  .replace(/""/g, '"');
                
                const jsonProps = JSON.parse(fixedJson);
                zoom = jsonProps.zoom || zoom;
                console.log("Successfully parsed with fixed JSON:", fixedJson);
              } catch (fixError) {
                // If all parsing attempts fail, use default zoom
                console.warn("Could not parse jsonProps even after fixing, using default zoom:", fixError);
              }
            }
            
            console.log("Return data==>",{
              editorMode: editorMode,
              mapMetadata: mapMedata,
              zoom: zoom,
            });
            return {
              editorMode: editorMode,
              mapMetadata: mapMedata,
              zoom: zoom,
            };
          });
          result = json(data);
        } catch (e) {
          // If the issue is an auth error, it needs to be redirect to login.
          const error = e as ErrorInfo;
          if (!error.isAuth) {
            console.warn(`Map could not be loaded`);
            console.warn(e);
            throw e;
          }
        }
        break;
      }
      case 'view-public': {
        const data = await client.fetchMapMetadata(mapId).then((mapMedata) => {
          // Parse jsonProps safely with fallback
          let zoom = 0.8; // Default zoom value
          try {
            // Try standard JSON parsing first
            if (mapMedata.jsonProps) {
              const jsonProps = JSON.parse(mapMedata.jsonProps);
              zoom = jsonProps.zoom || zoom;
            }
          } catch (jsonError) {
            console.warn("Invalid JSON format in jsonProps for public view, attempting to fix:", jsonError);
            
            // Try to fix common JSON formatting issues
            try {
              // Fix for format like {zoom:0.8} (missing quotes around property names)
              const fixedJson = mapMedata.jsonProps
                .replace(/\{/g, '{"')
                .replace(/:/g, '":')
                .replace(/":/g, '":')
                .replace(/,/g, ',"')
                .replace(/,"/g, ',"')
                .replace(/""/g, '"');
              
              const jsonProps = JSON.parse(fixedJson);
              zoom = jsonProps.zoom || zoom;
              console.log("Successfully parsed with fixed JSON:", fixedJson);
            } catch (fixError) {
              // If all parsing attempts fail, use default zoom
              console.warn("Could not parse jsonProps even after fixing, using default zoom:", fixError);
            }
          }
          
          return {
            editorMode: 'viewonly-public',
            mapMetadata: mapMedata,
            zoom: zoom,
          };
        });
        result = json(data);
        break;
      }
      default: {
        const exhaustiveCheck: never = pageMode;
        throw new Error(exhaustiveCheck);
      }
    }

    // If result has not been set, redict to the login with the original url
    if (!result) {
      const url = window.location.pathname;
      result = new Response('Map could not be loaded, redirect to login.', {
        status: 302,
        headers: {
          Location: `/c/login?redirect=${url}`,
        },
      });
    }

    return result;
  };
};
